import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * AI Module Service Stores
 * Services for AI/ML operations including OpenAI, NLP, RAG, and vector storage
 */
export const aiStores: ServiceStoreConfig[] = [
  {
    name: "openai",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.openaiStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalRequests = entities.length;
          const recentRequests = entities.filter(
            (req: Record<string, unknown>) => {
              const reqDate = new Date(req.createdAt as string);
              const hourAgo = new Date();
              hourAgo.setHours(hourAgo.getHours() - 1);
              return reqDate > hourAgo;
            }
          ).length;

          return [
            {
              title: "Total Requests",
              value: () => totalRequests.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Recent (1h)",
              value: () => recentRequests.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "ingestion",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.ingestionStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalIngested = entities.length;
          const inProgress = entities.filter(
            (item: Record<string, unknown>) =>
              item.status === "processing" || item.status === "pending"
          ).length;
          const completed = entities.filter(
            (item: Record<string, unknown>) =>
              item.status === "completed"
          ).length;
          const failed = entities.filter(
            (item: Record<string, unknown>) =>
              item.status === "failed"
          ).length;

          return [
            {
              title: "Total Ingested",
              value: () => totalIngested.toString(),
              valueColor: "primary.main",
            },
            {
              title: "In Progress",
              value: () => inProgress.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Completed",
              value: () => completed.toString(),
              valueColor: "success.main",
            },
            {
              title: "Failed",
              value: () => failed.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "nlp-service",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.nlpServiceStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalProcessed = entities.length;
          const todayProcessed = entities.filter(
            (item: Record<string, unknown>) => {
              const processDate = new Date(item.createdAt as string);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return processDate >= today;
            }
          ).length;

          return [
            {
              title: "Total Processed",
              value: () => totalProcessed.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Today",
              value: () => todayProcessed.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "openai-rag",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.openaiRagStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalQueries = entities.length;
          const successfulQueries = entities.filter(
            (query: Record<string, unknown>) =>
              query.status === "success" || query.retrieved === true
          ).length;

          return [
            {
              title: "Total Queries",
              value: () => totalQueries.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Successful",
              value: () => successfulQueries.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "processing",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.processingStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalJobs = entities.length;
          const activeJobs = entities.filter(
            (job: Record<string, unknown>) =>
              job.status === "processing" || job.status === "running"
          ).length;
          const queuedJobs = entities.filter(
            (job: Record<string, unknown>) =>
              job.status === "queued" || job.status === "pending"
          ).length;
          const completedJobs = entities.filter(
            (job: Record<string, unknown>) =>
              job.status === "completed"
          ).length;

          return [
            {
              title: "Total Jobs",
              value: () => totalJobs.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeJobs.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Queued",
              value: () => queuedJobs.toString(),
              valueColor: "info.main",
            },
            {
              title: "Completed",
              value: () => completedJobs.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "querying",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.queryingStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalQueries = entities.length;
          const recentQueries = entities.filter(
            (query: Record<string, unknown>) => {
              const queryDate = new Date(query.createdAt as string);
              const hourAgo = new Date();
              hourAgo.setHours(hourAgo.getHours() - 1);
              return queryDate > hourAgo;
            }
          ).length;

          return [
            {
              title: "Total Queries",
              value: () => totalQueries.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Recent (1h)",
              value: () => recentQueries.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "search-query-builder",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.searchQueryBuilderStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalQueries = entities.length;
          const complexQueries = entities.filter(
            (query: Record<string, unknown>) =>
              query.complexity === "high" || (query.filters as Array<unknown>)?.length > 3
          ).length;

          return [
            {
              title: "Total Queries",
              value: () => totalQueries.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Complex",
              value: () => complexQueries.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "ticket-classifier",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.ticketClassifierStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalClassified = entities.length;
          const highPriority = entities.filter(
            (ticket: Record<string, unknown>) =>
              ticket.priority === "high" || ticket.severity === "critical"
          ).length;
          const autoClassified = entities.filter(
            (ticket: Record<string, unknown>) =>
              ticket.classifiedBy === "ai" || ticket.autoClassified === true
          ).length;

          return [
            {
              title: "Total Classified",
              value: () => totalClassified.toString(),
              valueColor: "primary.main",
            },
            {
              title: "High Priority",
              value: () => highPriority.toString(),
              valueColor: "error.main",
            },
            {
              title: "Auto-Classified",
              value: () => autoClassified.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "universal-indexer",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.universalIndexerStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalIndexed = entities.length;
          const pendingIndex = entities.filter(
            (item: Record<string, unknown>) =>
              item.status === "pending" || item.indexed === false
          ).length;

          return [
            {
              title: "Total Indexed",
              value: () => totalIndexed.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Pending",
              value: () => pendingIndex.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
    },
  },
  {
    name: "vector-storage",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.vectorStorageStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalVectors = entities.length;
          const collections = new Set(
            entities.map((item: Record<string, unknown>) => item.collection)
          ).size;

          return [
            {
              title: "Total Vectors",
              value: () => totalVectors.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Collections",
              value: () => collections.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
    },
  },
  // NOTE: The following AI optimization stores have custom ViewStores and routes
  // in src/views/ai/. They use serviceView: { enableServiceView: false } because
  // the backend services are not yet implemented. Once implemented, you can enable
  // the auto-generated ServiceView routes by setting enableServiceView: false.
  {
    name: "ai-mix-optimization",
    options: {
      enableStorage: true,
      enableErrorNotifications: false, // Suppress errors until backend is ready
      serviceView: {
        enableServiceView: false, // Custom routes in src/views/ai/MixOptimization/
      },
    },
  },
  {
    name: "ai-demand-forecast",
    options: {
      enableStorage: true,
      enableErrorNotifications: false, // Suppress errors until backend is ready
      serviceView: {
        enableServiceView: false, // Custom routes in src/views/ai/DemandForecast/
      },
    },
  },
  {
    name: "ai-carbon-optimization",
    options: {
      enableStorage: true,
      enableErrorNotifications: false, // Suppress errors until backend is ready
      serviceView: {
        enableServiceView: false, // Custom routes in src/views/ai/CarbonOptimization/
      },
    },
  },
];